/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, Progress } from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Box, Typography, makeStyles } from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import { reposInScope, relativeAge } from './ScopedPullRequestsCard';
import { FilterChips } from './FilterChips';
import { useHomeScope } from './HomeScopeContext';

// The card samples a bounded set of scoped repos -- matches the backend cap
// and keeps the per-repo fan-out (3 GitHub calls each) sane.
const CARD_REPO_CAP = 15;

type ServiceHealth = {
  repo: string;
  owner: string;
  defaultBranch: string | null;
  ci: {
    status: string | null;
    conclusion: string | null;
    url: string | null;
    at: string | null;
  } | null;
  release: { tag: string; at: string | null } | null;
};

// Map a workflow run's (status, conclusion) onto one of four health states.
// completed+success = ok; completed+<bad> = fail; not-yet-completed = running;
// everything else (skipped/neutral/no-CI) = neutral.
type CiState = 'ok' | 'fail' | 'running' | 'neutral';
export const ciStateOf = (ci: ServiceHealth['ci']): CiState => {
  if (!ci) return 'neutral';
  if (ci.status && ci.status !== 'completed') return 'running';
  switch (ci.conclusion) {
    case 'success':
      return 'ok';
    case 'failure':
    case 'timed_out':
    case 'startup_failure':
      return 'fail';
    default:
      return 'neutral';
  }
};

const CI_LABEL: Record<CiState, string> = {
  ok: 'Passing',
  fail: 'Failing',
  running: 'Running',
  neutral: 'No CI',
};
const CI_ICON: Record<CiState, typeof CheckCircleOutlineIcon> = {
  ok: CheckCircleOutlineIcon,
  fail: ErrorOutlineIcon,
  running: AutorenewIcon,
  neutral: RemoveCircleOutlineIcon,
};
// Rank used both to order rows (most actionable first) and the filter chips.
const CI_ORDER: CiState[] = ['fail', 'running', 'ok', 'neutral'];

// 6-digit hex (#rrggbb) -> rgba() tint. The semantic palette colours differ per
// theme mode, so the badge tint derives from the live token rather than a
// hard-coded rgba (the pattern the sibling chips use for the single, fixed
// brand colour). Every semantic token is 6-digit hex today; if one ever isn't,
// fall back to a transparent fill (the icon + coloured label stay legible)
// rather than returning the raw colour as an opaque, low-contrast background.
const hexToRgba = (hex: string, alpha: number): string => {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim());
  if (!m) return 'transparent';
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const useStyles = makeStyles(theme => {
  const colorFor: Record<CiState, string> = {
    ok: theme.palette.success.main,
    fail: theme.palette.error.main,
    running: theme.palette.warning.main,
    neutral: theme.palette.text.secondary,
  };
  return {
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(0.75, 1),
      margin: theme.spacing(0, -1),
      borderRadius: theme.shape.borderRadius,
      transition: 'background-color 120ms cubic-bezier(0.25, 1, 0.5, 1)',
      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      '&:hover': { backgroundColor: theme.palette.action.hover },
    },
    // Leading status badge: icon + word, tinted in the status colour. Fixed
    // min-width so repo names line up in a scannable column.
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      flexShrink: 0,
      minWidth: 92,
      padding: theme.spacing(0.25, 1),
      borderRadius: 999,
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    badgeIcon: { fontSize: 16 },
    ok: { color: colorFor.ok, backgroundColor: hexToRgba(colorFor.ok, 0.14) },
    fail: { color: colorFor.fail, backgroundColor: hexToRgba(colorFor.fail, 0.14) },
    running: {
      color: colorFor.running,
      backgroundColor: hexToRgba(colorFor.running, 0.14),
    },
    neutral: {
      color: colorFor.neutral,
      backgroundColor: theme.palette.action.hover,
    },
    spin: {
      animation: '$ocpHealthSpin 1.4s linear infinite',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    '@keyframes ocpHealthSpin': {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    repo: {
      flex: 1,
      minWidth: 0,
      fontWeight: 500,
      fontSize: '0.875rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    meta: {
      flexShrink: 0,
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      whiteSpace: 'nowrap',
      fontVariantNumeric: 'tabular-nums',
    },
    empty: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      padding: theme.spacing(1, 0),
    },
  };
});

/**
 * Home "Service health" card (H3 / PLAT-252). For the repos in the user's
 * current scope, shows each service's default-branch CI status and the age of
 * its latest release -- the deploy/CI "metric family" the Home page never had.
 * Reads from service-health-reader (App credentials), so it works for any
 * signed-in user without per-user GitHub OAuth, exactly like the PR card.
 *
 * Status reads at a glance: a colour + icon + word badge per row (not a bare
 * dot), and a status-facet chip row (Passing / Failing / Running / No CI, with
 * live counts) that doubles as the at-a-glance summary and a filter.
 */
export const ServiceHealthCardBody = () => {
  const { scopedEntities } = useHomeScope();
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const repos = useMemo(
    () => reposInScope(scopedEntities, CARD_REPO_CAP),
    [scopedEntities],
  );
  const repoKey = repos.join('|');

  const [services, setServices] = useState<ServiceHealth[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState<CiState | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Scope changed: drop any active status filter so a stale facet can't
    // strand the card on an empty filtered view (the clearing chip disappears
    // once that state's count hits 0 in the new scope).
    setFilter(null);
    if (repos.length === 0) {
      setServices([]);
      setFailed(false);
      return undefined;
    }
    setServices(null);
    setFailed(false);
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('service-health-reader');
        const qs = repos
          .map(r => `repo=${encodeURIComponent(r)}`)
          .join('&');
        const res = await fetchApi.fetch(`${base}/health?${qs}`);
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as { services: ServiceHealth[] };
        if (!cancelled) setServices(body.services ?? []);
      } catch {
        if (!cancelled) {
          setServices([]);
          setFailed(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [repoKey, discoveryApi, fetchApi, repos]);

  // Per-state counts feed both the facet chips and the at-a-glance summary.
  const counts = useMemo(() => {
    const c: Record<CiState, number> = { ok: 0, fail: 0, running: 0, neutral: 0 };
    for (const svc of services ?? []) c[ciStateOf(svc.ci)] += 1;
    return c;
  }, [services]);

  // Sort by actionability (failing first), then name; apply the active filter.
  const ordered = useMemo(() => {
    if (!services) return [];
    const rank = Object.fromEntries(CI_ORDER.map((s, i) => [s, i])) as Record<
      CiState,
      number
    >;
    return [...services]
      .filter(svc => !filter || ciStateOf(svc.ci) === filter)
      .sort((a, b) => {
        const r = rank[ciStateOf(a.ci)] - rank[ciStateOf(b.ci)];
        return r !== 0 ? r : a.repo.localeCompare(b.repo);
      });
  }, [services, filter]);

  // One FilterChips group, same control the "Services you own" card uses. Only
  // states actually present show as facets (a uniform-status scope shows none).
  const statusGroups = useMemo(
    () => [
      {
        key: 'status',
        value: filter,
        onChange: (next: string | null) => setFilter(next as CiState | null),
        items: CI_ORDER.filter(s => counts[s] > 0).map(s => ({
          key: s,
          label: CI_LABEL[s],
          count: counts[s],
        })),
      },
    ],
    [filter, counts],
  );

  // Plain render helper, not a nested component: declaring a component inside
  // render gives it a fresh identity each pass and remounts the subtree.
  const renderBadge = (state: CiState) => {
    const Icon = CI_ICON[state];
    return (
      <Box component="span" className={`${classes.badge} ${classes[state]}`}>
        <Icon
          className={`${classes.badgeIcon}${
            state === 'running' ? ` ${classes.spin}` : ''
          }`}
          aria-hidden
        />
        {CI_LABEL[state]}
      </Box>
    );
  };

  const renderBody = () => {
    if (services === null) return <Progress />;
    if (failed) {
      return (
        <Typography className={classes.empty}>
          Couldn't load service health right now.
        </Typography>
      );
    }
    // No scoped repos, or every scoped repo was dropped by the backend owner
    // allow-list (services: []).
    if ((services?.length ?? 0) === 0) {
      return (
        <Typography className={classes.empty}>
          No services in the current scope.
        </Typography>
      );
    }
    // Services exist but the active filter excludes them all.
    if (ordered.length === 0) {
      return (
        <Typography className={classes.empty}>
          No {filter ? CI_LABEL[filter].toLowerCase() : ''} services in this
          scope.
        </Typography>
      );
    }
    return ordered.map(svc => {
      const state = ciStateOf(svc.ci);
      const releaseText = svc.release
        ? `${svc.release.tag}${
            svc.release.at ? ` · ${relativeAge(svc.release.at)}` : ''
          }`
        : 'no release';
      return (
        <Box key={`${svc.owner}/${svc.repo}`} className={classes.row}>
          {renderBadge(state)}
          {svc.ci?.url ? (
            <Link to={svc.ci.url} className={classes.repo}>
              {svc.repo}
            </Link>
          ) : (
            <span className={classes.repo}>{svc.repo}</span>
          )}
          <Typography component="span" className={classes.meta}>
            {releaseText}
          </Typography>
        </Box>
      );
    });
  };

  return (
    <>
      {services && services.length > 0 && (
        <Box mb={1.5}>
          <FilterChips
            ariaLabel="Filter services by status"
            groups={statusGroups}
          />
        </Box>
      )}
      {renderBody()}
    </>
  );
};
